import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PayCashierApi as CashierApi } from "./PayCashierApi";
import {
  ALIPAY_DISABLED_HINT,
  AUTO_SELECT_WECHAT_ON_WEB,
  PAY_STATUS_POLL_MS,
  resolveCashierOrderListUrl,
} from "./CashierConfig";
import { resolveEnterpriseDisabledToast, isEnterprisePayMethod } from "./cashierEnterpriseMethodDisplay";
import { CashierErrorView } from "./CashierErrorView";
import { CashierLoadingView } from "./CashierLoadingView";
import type {
  PayCashierMethodOption,
  PayCashierSessionView,
  PayMethodCode,
} from "./CashierType";
import { CashierH5View } from "./CashierH5View";
import { CashierWebView } from "./CashierWebView";
import { detectClientSurface, type ClientSurface } from "./utils/device";
import { showToast } from "./utils/toast";
import {
  resolveCashierExpiredError,
  resolveCashierLocalExpiredPrompt,
  type CashierExpiredPrompt,
} from "./cashierExpiredError";
import {
  isCashierOrderPaid,
  isCashierOrderPayable,
  resolveCashierBlockedFromSession,
} from "./cashierOrderStatus";

const CashierPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionToken = (searchParams.get("t") ?? "").trim();
  const returnUrlParam = (searchParams.get("returnUrl") ?? "").trim();

  const [surface, setSurface] = useState<ClientSurface>(() => detectClientSurface());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [session, setSession] = useState<PayCashierSessionView | null>(null);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [wechatNativeCodeUrl, setWechatNativeCodeUrl] = useState("");
  const [payBlocked, setPayBlocked] = useState<CashierExpiredPrompt | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wechatAutoStartedRef = useRef(false);

  const applySessionState = useCallback((next: PayCashierSessionView) => {
    if (isCashierOrderPaid(next)) {
      setPaid(true);
      setPayBlocked(null);
      return;
    }
    const blocked = resolveCashierBlockedFromSession(next);
    if (blocked) {
      setPayBlocked(blocked);
      setWechatNativeCodeUrl("");
      return;
    }
    setPayBlocked(null);
  }, []);

  const effectiveReturnUrl = useMemo(() => {
    if (returnUrlParam) {
      return returnUrlParam;
    }
    return session?.returnUrl?.trim() ?? "";
  }, [returnUrlParam, session?.returnUrl]);

  const paySuccessRedirectUrl = useMemo(() => resolveCashierOrderListUrl(), []);

  useEffect(() => {
    const onResize = () => setSurface(detectClientSurface());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!sessionToken) {
      setError("缺少收银会话参数");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError("");
      const res = await CashierApi.getSession(sessionToken);
      if (cancelled) {
        return;
      }
      if (typeof res === "string") {
        setError(res);
        setSession(null);
      } else {
        setSession(res);
        applySessionState(res);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionToken, applySessionState]);

  useEffect(() => {
    if (!session || paid) {
      return;
    }
    applySessionState(session);
  }, [session, paid, applySessionState]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(() => {
      void (async () => {
        const sessionRes = await CashierApi.getSession(sessionToken);
        if (typeof sessionRes !== "string") {
          setSession(sessionRes);
          applySessionState(sessionRes);
          if (isCashierOrderPaid(sessionRes)) {
            stopPoll();
            setPaid(true);
            setPaying(false);
            window.setTimeout(() => {
              window.location.href = paySuccessRedirectUrl;
            }, 800);
            return;
          }
          if (!isCashierOrderPayable(sessionRes)) {
            setPaying(false);
          }
        }
        const res = await CashierApi.queryPayStatus(sessionToken);
        if (typeof res === "string") {
          return;
        }
        if (res.paid) {
          stopPoll();
          setPaid(true);
          setPaying(false);
          window.setTimeout(() => {
            window.location.href = paySuccessRedirectUrl;
          }, 800);
        }
      })();
    }, PAY_STATUS_POLL_MS);
  }, [sessionToken, paySuccessRedirectUrl, stopPoll, applySessionState]);

  useEffect(() => {
    if (paid || !session || session.status === "paid") {
      return undefined;
    }
    startPoll();
    return stopPoll;
  }, [paid, session, startPoll, stopPoll]);

  const handleBack = useCallback(() => {
    const url = effectiveReturnUrl || session?.quitUrl?.trim();
    if (url) {
      window.location.href = url;
      return;
    }
    window.history.back();
  }, [effectiveReturnUrl, session?.quitUrl]);

  const handleSelectMethod = useCallback(
    async (method: PayCashierMethodOption) => {
      if (paid || paying || payBlocked) {
        return;
      }
      if (!method.enabled) {
        if (isEnterprisePayMethod(method)) {
          showToast(resolveEnterpriseDisabledToast(method));
        } else {
          showToast(method.disabledHint?.trim() || ALIPAY_DISABLED_HINT);
        }
        return;
      }
      const code = method.code as PayMethodCode;
      if (code === "enterprise") {
        const ok = window.confirm("确认使用企业余额支付该订单？");
        if (!ok) {
          return;
        }
      }
      if (session && !isCashierOrderPayable(session)) {
        setPayBlocked(
          resolveCashierBlockedFromSession(session) ??
            resolveCashierLocalExpiredPrompt()
        );
        setWechatNativeCodeUrl("");
        return;
      }
      setPaying(true);
      const res = await CashierApi.submitPay(sessionToken, code, surface);
      if (typeof res === "string") {
        const expired = resolveCashierExpiredError(res);
        if (expired.isExpired) {
          setPayBlocked(expired);
          setWechatNativeCodeUrl("");
        } else {
          showToast(res);
        }
        setPaying(false);
        return;
      }
      if (!res.success) {
        const message = res.errorMessage?.trim() || "支付发起失败";
        const expired = resolveCashierExpiredError(message);
        if (expired.isExpired) {
          setPayBlocked(expired);
          setWechatNativeCodeUrl("");
        } else {
          showToast(message);
        }
        setPaying(false);
        return;
      }
      const credType = (res.checkoutCredentialType ?? "").trim().toLowerCase();
      if (credType === "wechatnative" && res.wechatNativeCodeUrl?.trim()) {
        setWechatNativeCodeUrl(res.wechatNativeCodeUrl.trim());
        setPaying(false);
        return;
      }
      if (credType === "enterprisepaid") {
        setPaid(true);
        setPaying(false);
        window.setTimeout(() => {
          window.location.href = paySuccessRedirectUrl;
        }, 800);
        return;
      }
      const jump = res.redirectUrl?.trim();
      if (jump) {
        window.location.href = jump;
        return;
      }
      showToast("未获取到支付凭据");
      setPaying(false);
    },
    [paid, paying, payBlocked, session, sessionToken, surface, paySuccessRedirectUrl]
  );

  useEffect(() => {
    if (
      !AUTO_SELECT_WECHAT_ON_WEB ||
      surface !== "web" ||
      paid ||
      !session ||
      payBlocked ||
      wechatNativeCodeUrl ||
      wechatAutoStartedRef.current ||
      !isCashierOrderPayable(session)
    ) {
      return;
    }
    const wechat = session.payMethods?.find((m) => m.code === "wechat" && m.enabled);
    if (!wechat) {
      return;
    }
    wechatAutoStartedRef.current = true;
    void handleSelectMethod(wechat);
  }, [surface, paid, session, payBlocked, wechatNativeCodeUrl, handleSelectMethod]);

  if (loading) {
    return <CashierLoadingView />;
  }
  if (error || !session) {
    const expired = resolveCashierExpiredError(error);
    return (
      <CashierErrorView
        returnUrl={returnUrlParam || effectiveReturnUrl}
        title={expired.isExpired ? expired.title : "请稍后"}
        description={
          expired.isExpired
            ? expired.description
            : error || "收银台暂时无法打开，请返回商户页面重试"
        }
      />
    );
  }

  const viewProps = {
    session,
    paying,
    paid,
    payBlocked,
    wechatNativeCodeUrl: surface === "web" ? wechatNativeCodeUrl : undefined,
    onSelectMethod: handleSelectMethod,
    onBack: effectiveReturnUrl || session.quitUrl ? handleBack : undefined,
  };

  return surface === "h5" ? (
    <CashierH5View {...viewProps} />
  ) : (
    <CashierWebView {...viewProps} />
  );
};

export default CashierPage;
